import { PendingBotResponse, UserPresenceStatus } from "@/types/presence"
import { UserRef } from "@/types/user"
import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit"
import uniq from "lodash/uniq"

import { getConstantEmptyArray } from "@/lib/utils/collections"
import { present } from "@/lib/utils/presence"
import { afterTimestamp } from "../date-utils"
import { tripsApi } from "./api/trips-api"
import { RootState } from "./redux-store"
import userSlice, { selectChatId } from "./user-slice"

const selectCurrentUserRef = (state: RootState) =>
  state.userSlice.user?.user_ref
const selectChatTrip = (state: RootState) => state.userSlice.chat.trip

export interface PresenceSliceState {
  tripId?: number
  userPresenceStatus?: UserPresenceStatus[]
  pendingBotResponses?: PendingBotResponse[]
  idle: boolean
}

export const presenceSliceInitialState: PresenceSliceState = {
  idle: false,
}

const selectSliceState = (state: RootState) => state.presenceSlice

export const selectUserPresenceStatuses = createSelector(
  selectSliceState,
  (state) =>
    state.userPresenceStatus || getConstantEmptyArray<UserPresenceStatus>()
)

export const selectIdle = createSelector(
  selectSliceState,
  (state) => state.idle
)

export const selectCurrentUserPresenceStatus = createSelector(
  [selectUserPresenceStatuses, selectCurrentUserRef],
  (statuses, currentUserRef) =>
    statuses.find((s) => s.user_ref === currentUserRef)
)

export const selectRefsOfUsersWhoArePresent = createSelector(
  [selectUserPresenceStatuses],
  (statuses) => statuses.map((status) => status.user_ref)
)

export const selectIsCurrentUserTyping = createSelector(
  selectCurrentUserPresenceStatus,
  (status) => status?.presence_type === "typing"
)

const selectCollaboratorsFromCache = createSelector(
  [(state: RootState) => state, selectChatTrip],
  (state, trip) => {
    if (!trip?.ref || !trip.collaboration) return []
    return (
      tripsApi.endpoints.getCollaborators.select(trip.ref)(state).data ?? []
    )
  }
)

export const selectOtherUsersWhoAreTyping = createSelector(
  [
    selectUserPresenceStatuses,
    selectCollaboratorsFromCache,
    selectCurrentUserRef,
  ],
  (statuses, collaborators, currentUserRef) => {
    // Match statuses to collaborators so we can return PublicUser objects.
    const userRefs = uniq(
      statuses
        .filter((status) => status.presence_type === "typing")
        .map((status) => status.user_ref)
        .filter((userRef) => userRef !== currentUserRef)
    )
    return userRefs
      .map((userRef) => collaborators.find((c) => c.user.user_ref === userRef))
      .map((collaborator) => collaborator?.user)
      .filter(present)
  }
)

// Pending responses in the current chat. One without a chat_id came from a
// server that predates per-chat events, so it can't be placed and counts here.
const selectPendingBotResponses = createSelector(
  [selectSliceState, selectChatId],
  (state, chatId) =>
    state.pendingBotResponses?.filter(
      (pb) => pb.chat_id === undefined || pb.chat_id === chatId
    ) || getConstantEmptyArray<PendingBotResponse>()
)

export const selectBotIsRespondingInChat = createSelector(
  [selectPendingBotResponses],
  (pendingBotResponses) => pendingBotResponses.length > 0
)

export const selectStreamIdsBotIsRespondingToMe = createSelector(
  [selectPendingBotResponses, selectCurrentUserRef],
  (pendingBotResponses, currentUserRef) =>
    pendingBotResponses
      .filter((pb) => pb.responding_to_user_ref === currentUserRef)
      .map((pb) => pb.stream_id)
)

export const selectPendingBotResponse = (state: RootState, streamId: string) =>
  selectSliceState(state).pendingBotResponses?.find(
    (pb) => pb.stream_id === streamId
  )
export const selectUsersBotIsRespondingTo = createSelector(
  [selectPendingBotResponses, selectCollaboratorsFromCache],
  (pendingBotResponses, collaborators) => {
    const userRefs = uniq(
      pendingBotResponses
        .map((pendingBotResponse) => pendingBotResponse.responding_to_user_ref)
        .filter(present)
    )
    return userRefs
      .map((userRef) => collaborators.find((c) => c.user.user_ref === userRef))
      .map((collaborator) => collaborator?.user)
      .filter(present)
  }
)

const presenceSlice = createSlice({
  name: "presence",
  initialState: presenceSliceInitialState,
  reducers: {
    setIdle: (state, action: PayloadAction<boolean>) => {
      state.idle = action.payload
    },

    updateUserPresence: (state, action: PayloadAction<UserPresenceStatus>) => {
      const newStatus = action.payload
      state.userPresenceStatus = state.userPresenceStatus || []

      const index = state.userPresenceStatus.findIndex(
        (s) => s.user_ref === newStatus.user_ref
      )
      if (index === -1) {
        state.userPresenceStatus.push(newStatus)
      } else if (
        afterTimestamp(
          newStatus.timestamp,
          state.userPresenceStatus[index].timestamp
        )
      ) {
        state.userPresenceStatus[index] = newStatus
      }
    },

    removeUserPresence: (state, action: PayloadAction<UserRef>) => {
      const userRef = action.payload
      if (!state.userPresenceStatus) return

      const index = state.userPresenceStatus?.findIndex(
        (s) => s.user_ref === userRef
      )
      if (index !== -1) {
        state.userPresenceStatus.splice(index, 1)
      }
    },

    addPendingBotResponse: (
      state,
      action: PayloadAction<PendingBotResponse>
    ) => {
      state.pendingBotResponses ||= []
      const newResponse = action.payload
      const index = state.pendingBotResponses.findIndex(
        (br) => br.stream_id === newResponse.stream_id
      )
      if (index === -1) {
        state.pendingBotResponses.push(newResponse)
      } else {
        state.pendingBotResponses[index] = newResponse
      }
    },

    removePendingBotResponse: (state, action: PayloadAction<string>) => {
      const streamId = action.payload
      if (state.pendingBotResponses) {
        const index = state.pendingBotResponses.findIndex(
          (br) => br.stream_id === streamId
        )
        if (index !== -1) {
          state.pendingBotResponses.splice(index, 1)
        }
      }
    },
  },

  extraReducers: (builder) => {
    builder
      // When the chat changes, make sure we throw away any presence state
      // that no longer applies.
      // TODO: are there other ways the current trip id can change?
      .addCase(userSlice.actions.setChat, (state, action) => {
        const newChat = action.payload
        const newTripId = newChat.trip?.id
        if (newTripId !== state.tripId) {
          state.tripId = newTripId
          delete state.userPresenceStatus
          delete state.pendingBotResponses
        }
      })
      .addCase(userSlice.actions.addOrUpdateMessages, (state, action) => {
        const botMessages = action.payload.messages?.filter(
          (m) => m.type === "bot"
        )
        if (
          state.pendingBotResponses &&
          botMessages &&
          botMessages.length > 0
        ) {
          // remove any pending bot responses that match the incoming
          // persistent bot messages.
          state.pendingBotResponses = state.pendingBotResponses.filter(
            (pb) => !botMessages.some((m) => m.stream_id === pb.stream_id)
          )
        }
      })
  },
})

export const {
  setIdle,
  updateUserPresence,
  removeUserPresence,
  addPendingBotResponse,
  removePendingBotResponse,
} = presenceSlice.actions

export default presenceSlice.reducer
